/**
 * React Query hook for fetching ShowNotes blocks
 */

import { useQuery } from '@tanstack/react-query'
import httpClient, { pluckData } from 'src/utilities/http-client'
import { ShowNotesBlock } from '../types'

/**
 * Fetch all blocks for a show notes from the API
 */
async function fetchShowNotesBlocks(
  showNotesID: string,
  basePath: string,
): Promise<ShowNotesBlock[]> {
  let response = await httpClient.get(`/api/${basePath}/${showNotesID}/blocks`)

  let data = pluckData(response)

  // Handle the backend's wrapped format: [{ textBlock: {...} }, { imageBlock: {...} }, { pdfBlock: {...} }]
  if (
    Array.isArray(data) &&
    data.length > 0 &&
    (data[0].textBlock || data[0].imageBlock || data[0].pdfBlock)
  ) {
    let unwrapped = data
      .map((item) => {
        if (item.textBlock) {
          return { ...item.textBlock, blockType: 'text' } as ShowNotesBlock
        }
        if (item.imageBlock) {
          let imageBlock = {
            ...item.imageBlock,
            blockType: 'image',
            // Ensure default values for image block properties
            caption: item.imageBlock.caption || '',
            altText: item.imageBlock.altText || '',
            widthPercent: item.imageBlock.widthPercent || 100,
            imageUrl: item.imageBlock.imageUrl || '',
          } as ShowNotesBlock
          return imageBlock
        }
        if (item.pdfBlock) {
          let pdfBlock = {
            ...item.pdfBlock,
            blockType: 'pdf',
            // caption is nullable, so preserve null/undefined
          } as ShowNotesBlock
          return pdfBlock
        }
        return null
      })
      .filter(Boolean) as ShowNotesBlock[]
    return unwrapped
  }

  // Handle flat format (if backend changes later)
  return data as ShowNotesBlock[]
}

/**
 * Hook to fetch show notes blocks with React Query
 */
export function useShowNotesBlocks(
  showNotesID: string,
  basePath: string,
  enabled: boolean = true,
) {
  let result = useQuery({
    queryKey: ['showNotesBlocks', basePath, showNotesID],
    queryFn: () => fetchShowNotesBlocks(showNotesID, basePath),
    enabled: enabled && !!showNotesID,
    staleTime: 0, // Always consider stale so refetches work immediately
    gcTime: 10 * 60 * 1000, // 10 minutes
  })

  return result
}
